"use client";

import { useEffect, useRef, useCallback } from "react";
import type { Dispatch } from "react";
import type { FunnelWizardState, FunnelTemplateId } from "@/lib/funnel-planner/schema";
import type { FunnelPlannerContent } from "@/lib/content/entries/funnel-planner";
import type { AppLocale } from "@/lib/funnel-planner/schema";
import { recommendTemplateId, getTemplate, defaultScenarioSnapshots } from "@/lib/funnel-planner/engine";
import {
  buildBlueprintChannels,
  buildBlueprintScenarios,
  buildBlueprintStages,
  FUNNEL_BLUEPRINT_PRESETS,
  type FunnelBlueprintPreset,
} from "@/lib/funnel-planner/blueprints";
import { channelLabel, funnelText, stageTypeLabel } from "@/lib/funnel-planner/i18n";
import { getStageTypeMeta } from "@/lib/funnel-planner/stageTypes";
import { TemplateCard } from "@/components/funnel-planner/ui/TemplateCard";
import { track } from "@/lib/posthog/events";
import {
  joinAuditTicketGroups,
  STEP_ONE_FIELD_INPUT_TICKETS,
  STEP_ONE_TEMPLATE_PICKER_SUPPORT_TICKETS,
} from "@/lib/funnel-planner/gamification-next200-step1-tickets";

type Action = (
  | { type: "SET_TEMPLATE"; templateId: FunnelTemplateId }
  | { type: "SET_STAGES"; stages: FunnelWizardState["stages"] }
  | { type: "SET_TRAFFIC_CHANNELS"; channels: FunnelWizardState["trafficChannels"] }
  | { type: "SET_PROJECTION_INPUTS"; inputs: FunnelWizardState["projectionInputs"] }
  | { type: "SET_SCENARIOS"; scenarios: FunnelWizardState["scenarios"] }
) & { source?: "automatic" };

const TEMPLATE_DISPLAY_ORDER: FunnelTemplateId[] = [
  "ecommerce-dtc",
  "b2b-enterprise",
  "saas-plg",
  "high-ticket-coaching",
  "franchise-multi-location",
  "ai-search-geo",
  "agency-white-label",
  "adult-compliance",
];
const TEMPLATE_PICKER_READINESS_TICKET =
  "P1-105001-interactive-step-01-template-picker-readiness-board";
const TEMPLATE_PICKER_OFFER_ASSEMBLY_TICKET =
  "P3-105002-interactive-step-01-template-picker-offer-assembly-configurator";
const TEMPLATE_PICKER_STEP_ONE_FIELD_TICKETS = joinAuditTicketGroups(
  "P3-085002-form-step-01-field-planner-input-086-chunked-multi-step-form-progre",
  STEP_ONE_FIELD_INPUT_TICKETS,
);
const STEP_ONE_OFFER_ASSEMBLY_CTA_TICKETS = [
  "P1-025001-cta-step-01-button-planner-action-026-offer-assembly-mini-cta-brief",
  "P1-026001-cta-step-01-button-planner-action-027-offer-assembly-mini-cta-brief",
  "P1-027001-cta-step-01-button-planner-action-028-offer-assembly-mini-cta-brief",
  "P1-028001-cta-step-01-button-planner-action-029-offer-assembly-mini-cta-brief",
  "P1-029001-cta-step-01-button-planner-action-030-offer-assembly-mini-cta-brief",
  "P1-030001-cta-step-01-button-planner-action-031-offer-assembly-mini-cta-brief",
  "P1-031001-cta-step-01-button-planner-action-032-offer-assembly-mini-cta-brief",
  "P1-032001-cta-step-01-button-planner-action-033-offer-assembly-mini-cta-brief",
  "P1-033001-cta-step-01-button-planner-action-034-offer-assembly-mini-cta-brief",
  "P1-034001-cta-step-01-button-planner-action-035-offer-assembly-mini-cta-brief",
  "P1-035001-cta-step-01-button-planner-action-036-offer-assembly-mini-cta-brief",
  "P1-036001-cta-step-01-button-planner-action-037-offer-assembly-mini-cta-brief",
  "P1-037001-cta-step-01-button-planner-action-038-offer-assembly-mini-cta-brief",
  "P1-038001-cta-step-01-button-planner-action-039-offer-assembly-mini-cta-brief",
  "P1-039001-cta-step-01-button-planner-action-040-offer-assembly-mini-cta-brief",
  "P1-040001-cta-step-01-button-planner-action-041-offer-assembly-mini-cta-brief",
  "P1-041001-cta-step-01-button-planner-action-042-offer-assembly-mini-cta-brief",
  "P1-042001-cta-step-01-button-planner-action-043-offer-assembly-mini-cta-brief",
  "P1-043001-cta-step-01-button-planner-action-044-offer-assembly-mini-cta-brief",
  "P1-044001-cta-step-01-button-planner-action-045-offer-assembly-mini-cta-brief",
  "P1-045001-cta-step-01-button-planner-action-046-offer-assembly-mini-cta-brief",
  "P1-046001-cta-step-01-button-planner-action-047-offer-assembly-mini-cta-brief",
] as const;
const READINESS_SUPPORTED_STATES =
  "untouched_empty active_inprogress partial complete_result reduced_motion_static failure_empty";

function offerAssemblyCtaTicketsForIndex(index: number, visibleTemplateCount: number): string {
  return STEP_ONE_OFFER_ASSEMBLY_CTA_TICKETS
    .filter((_, ticketIndex) => ticketIndex % visibleTemplateCount === index)
    .join(";");
}

function getSortedTemplateIds(audience: FunnelWizardState["audience"]): FunnelTemplateId[] {
  if (audience === "adult") {
    return TEMPLATE_DISPLAY_ORDER;
  }
  return TEMPLATE_DISPLAY_ORDER.filter((id) => id !== "adult-compliance");
}

export function TemplatePickerStep({
  state,
  dispatch,
  content,
  locale,
}: {
  state: FunnelWizardState;
  dispatch: Dispatch<Action>;
  content: FunnelPlannerContent;
  locale: AppLocale;
}) {
  const recommended = recommendTemplateId(state.fitQuizBand, state.audience);
  const templateIds = getSortedTemplateIds(state.audience);
  const visibleBlueprints = FUNNEL_BLUEPRINT_PRESETS
    .filter((preset) => templateIds.includes(preset.templateId))
    .filter((preset) => state.templateId === null || preset.templateId === state.templateId)
    .slice(0, state.templateId === null ? 6 : 4);
  const gridRef = useRef<HTMLDivElement>(null);
  const hasAutoSelected = useRef(false);
  const templateReady = Boolean(state.templateId);

  const handleSelect = useCallback(
    (templateId: FunnelTemplateId, source?: "automatic") => {
      const template = getTemplate(templateId);
      dispatch({ type: "SET_TEMPLATE", templateId, source });
      dispatch({
        type: "SET_STAGES",
        source,
        stages: template.defaultStages.map((s) => {
          const nameKey = `templates.${templateId}.stages.${s.id}` as keyof FunnelPlannerContent;
          const name = (content[nameKey] as string) ?? s.id;
          return {
            ...s,
            name,
            stageType: s.stageType ?? "custom",
            publishedStatus: "draft" as const,
          };
        }),
      });
      track("funnel_planner_template_changed", {
        template_id: templateId,
        locale,
        step: 1,
      });
    },
    [dispatch, content, locale],
  );

  const handleBlueprintSelect = useCallback(
    (preset: FunnelBlueprintPreset) => {
      const labelForStageType = (stageType: string) => stageTypeLabel(content, stageType, getStageTypeMeta(stageType).label);
      const labelForChannel = (channelId: string) => channelLabel(content, channelId, channelId.replace(/_/g, " "));
      dispatch({ type: "SET_TEMPLATE", templateId: preset.templateId });
      dispatch({ type: "SET_STAGES", stages: buildBlueprintStages(preset, labelForStageType) });
      dispatch({ type: "SET_TRAFFIC_CHANNELS", channels: buildBlueprintChannels(preset, labelForChannel) });
      dispatch({ type: "SET_PROJECTION_INPUTS", inputs: preset.projectionInputs });
      dispatch({
        type: "SET_SCENARIOS",
        scenarios: buildBlueprintScenarios(
          preset,
          defaultScenarioSnapshots(),
          funnelText(content, "blueprintScenarioName", "Blueprint optimization"),
          funnelText(content, "blueprintScenarioNotes", "Preset optimization scenario for this blueprint."),
          [
            funnelText(content, "projectionScenarioAssumptionCvr", "CVR x{value}", {
              value: preset.scenarioLift.cvrMultiplier.toFixed(2),
            }),
            funnelText(content, "projectionScenarioAssumptionTraffic", "Traffic x{value}", {
              value: preset.scenarioLift.visitorMultiplier.toFixed(2),
            }),
            funnelText(content, "projectionScenarioAssumptionRevenue", "Revenue yield x{value}", {
              value: preset.scenarioLift.revenueMultiplier.toFixed(2),
            }),
          ],
        ),
      });
      track("funnel_planner_blueprint_applied", {
        template_id: preset.templateId,
        locale,
        blueprint_id: preset.id,
        blueprint_category: preset.category,
      });
    },
    [content, dispatch, locale],
  );

  useEffect(() => {
    if (!hasAutoSelected.current && state.templateId === null && recommended !== null) {
      hasAutoSelected.current = true;
      handleSelect(recommended, "automatic");
    }
  }, [recommended, state.templateId, handleSelect]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const keys = ["ArrowRight", "ArrowDown", "ArrowLeft", "ArrowUp"];
    if (!keys.includes(e.key)) return;
    e.preventDefault();

    const currentIndex = state.templateId !== null ? templateIds.indexOf(state.templateId) : -1;
    let nextIndex: number;

    if (e.key === "ArrowRight" || e.key === "ArrowDown") {
      nextIndex = currentIndex < templateIds.length - 1 ? currentIndex + 1 : 0;
    } else {
      nextIndex = currentIndex > 0 ? currentIndex - 1 : templateIds.length - 1;
    }

    const nextId = templateIds[nextIndex];
    handleSelect(nextId);

    if (gridRef.current) {
      const buttons = gridRef.current.querySelectorAll<HTMLButtonElement>("[role=radio]");
      buttons[nextIndex]?.focus();
    }
  };

  return (
    <div>
      {state.fitQuizBand && (
        <p
          role="status"
          style={{
            marginBottom: 12,
            padding: "8px 12px",
            borderRadius: 6,
            background: "rgba(37,99,235,0.10)",
            fontSize: 13,
            color: "#e2e8f0",
            borderLeft: "3px solid #2563eb",
          }}
        >
          {content.fitQuizAcknowledgment as string}
        </p>
      )}
      <p style={{ marginBottom: 12, fontSize: 13, color: "#94a3b8" }}>{content.templatePickerLabel}</p>
      <div
        ref={gridRef}
        role="radiogroup"
        aria-label={content.templatePickerLabel as string}
        onKeyDown={handleKeyDown}
        data-funnel-planner-template-picker="true"
        data-audit-ticket={joinAuditTicketGroups(
          TEMPLATE_PICKER_READINESS_TICKET,
          TEMPLATE_PICKER_OFFER_ASSEMBLY_TICKET,
          TEMPLATE_PICKER_STEP_ONE_FIELD_TICKETS,
          STEP_ONE_TEMPLATE_PICKER_SUPPORT_TICKETS,
        )}
        data-tier-one-family="readiness_board"
        data-gamification-current-state={templateReady ? "complete_result" : "active_inprogress"}
        data-gamification-reduced-motion-static="supported"
        data-gamification-supported-states={READINESS_SUPPORTED_STATES}
        data-readiness-ready-count={templateReady ? 1 : 0}
        data-readiness-total-count={1}
        data-readiness-tier={templateReady ? "template_ready" : "template_needed"}
        data-blockers-bucket={templateReady ? "none" : "one"}
        style={{ display: "grid", gap: "0.75rem", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 18rem), 1fr))" }}
      >
        <span className="sr-only" role="status" aria-live="polite">
          {templateReady
            ? funnelText(content, "gamificationTemplatePickerReady", "Template picker ready")
            : funnelText(content, "gamificationTemplatePickerWaiting", "Template picker waiting for a selection")}
        </span>
        {templateIds.map((id, index) => {
          const isSelected = state.templateId === id;
          const isFirst = index === 0;
          const tabIndexValue = isSelected ? 0 : state.templateId === null && isFirst ? 0 : -1;
          return (
            <TemplateCard
              key={id}
              templateId={id}
              content={content}
              selected={isSelected}
              recommended={recommended === id}
              auditTicket={offerAssemblyCtaTicketsForIndex(index, templateIds.length)}
              offerAssemblyCtaLabel={
                isSelected
                  ? (content.gamificationOfferAssemblyCtaSelected as string)
                  : (content.gamificationOfferAssemblyCtaScopeThis as string)
              }
              offerAssemblyCurrentState={isSelected ? "complete_result" : "active_inprogress"}
              onSelect={handleSelect}
              tabIndex={tabIndexValue}
            />
          );
        })}
      </div>
      <p style={{ marginTop: 12, fontSize: 12, color: "#94a3b8" }}>
        {state.fitQuizBand
          ? (content.templatePickerHelp as string)
          : (content.templatePickerHelpDirect as string)}
      </p>
      {state.templateId === null && (
        <p
          role="status"
          aria-live="polite"
          style={{ marginTop: 8, fontSize: 12, color: "#94a3b8" }}
        >
          {content.selectTemplateToAdvance as string}
        </p>
      )}
      {visibleBlueprints.length > 0 && (
        <section
          aria-labelledby="funnel-planner-blueprints-title"
          style={{
            marginTop: 24,
            borderTop: "1px solid rgba(148,163,184,0.22)",
            paddingTop: 18,
          }}
          data-funnel-planner-blueprints
        >
          <div style={{ marginBottom: 12 }}>
            <h2
              id="funnel-planner-blueprints-title"
              style={{ margin: 0, color: "#e2e8f0", fontSize: 16, fontWeight: 750 }}
            >
              {funnelText(content, "blueprintLibraryTitle", "Blueprint presets")}
            </h2>
            <p style={{ margin: "4px 0 0", color: "#94a3b8", fontSize: 13, lineHeight: 1.5 }}>
              {funnelText(content, "blueprintLibraryHelp", "Apply a reusable funnel structure with channel mix, editable planning inputs, and an optimization scenario.")}
            </p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 18rem), 1fr))", gap: 10 }}>
            {visibleBlueprints.map((preset) => {
              const label = funnelText(content, `blueprints.${preset.id}.label`, preset.id);
              const description = funnelText(content, `blueprints.${preset.id}.description`, preset.id);
              const descriptor = funnelText(content, `blueprints.${preset.id}.benchmark`, "");
              const category = funnelText(content, `blueprintCategories.${preset.category}`, preset.category);
              return (
                <button
                  key={preset.id}
                  type="button"
                  onClick={() => handleBlueprintSelect(preset)}
                  style={{
                    display: "grid",
                    gap: 6,
                    minHeight: 132,
                    border: "1px solid rgba(96,165,250,0.34)",
                    borderRadius: 8,
                    background: "rgba(15,23,42,0.64)",
                    color: "#e2e8f0",
                    textAlign: "start",
                    padding: 12,
                    cursor: "pointer",
                  }}
                  onMouseEnter={(event) => {
                    event.currentTarget.style.borderColor = "rgba(96,165,250,0.8)";
                    event.currentTarget.style.background = "rgba(30,41,59,0.82)";
                  }}
                  onMouseLeave={(event) => {
                    event.currentTarget.style.borderColor = "rgba(96,165,250,0.34)";
                    event.currentTarget.style.background = "rgba(15,23,42,0.64)";
                  }}
                >
                  <span style={{ color: "#93c5fd", fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: 0 }}>
                    {category}
                  </span>
                  <strong style={{ color: "#f8fafc", fontSize: 14, lineHeight: 1.25 }}>{label}</strong>
                  <span style={{ color: "#cbd5e1", fontSize: 12, lineHeight: 1.45 }}>{description}</span>
                  {descriptor && (
                    <span style={{ color: "#a7f3d0", fontSize: 11, fontWeight: 700 }}>{descriptor}</span>
                  )}
                </button>
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}
